import { badgeIcons } from "./AccessBadge";
import LText from "@components/LText";
import Animated, {
  SharedValue,
  useSharedValue,
  withTiming,
  Easing,
  useAnimatedStyle,
} from "react-native-reanimated";
import { AccessCarouselData, AccessType } from "./types";
import LBox from "@components/LBox";
import { Slide } from "@components/Exclusives/FeedAccess/AccessCarousel/parallax";
import { Platform, StyleSheet } from "react-native";
import AccessTypeMusic from "./AccessTypeMusic";
import { BlurView } from "expo-blur";
import LIcon from "@components/LIcon";
import { theme } from "@theme";
import LPressable from "@components/LPressable";
import { router } from "expo-router";
import { prefetchAccessDetailHeroImage } from "@utils/accesses/access";

const AccessCardContent = ({
  index,
  data,
  scrollX,
  onPress,
}: {
  index: number;
  data: AccessCarouselData;
  scrollX: SharedValue<number>;
  onPress?: () => void;
}) => {
  const scale = useSharedValue(1);

  const handlePressIn = () => {
    scale.value = withTiming(0.95, {
      duration: 75,
      easing: Easing.inOut(Easing.ease),
    });
  };

  const handlePressOut = () => {
    scale.value = withTiming(1, {
      duration: 150,
      easing: Easing.inOut(Easing.ease),
    });
  };

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const renderContent = () => {
    switch (data.type) {
      case AccessType.MUSIC:
        return <AccessTypeMusic data={data} />;
      case AccessType.TOP:
      case AccessType.DJ_SET:
      case AccessType.POST:
      case AccessType.VIDEO:
      case AccessType.EXPERIENCE:
      case AccessType.CHAT:
      case AccessType.EVENT:
      case AccessType.MERCH:
      case null:
        return (
          <LBox flex={1} pt="s3" px="s3">
            <LBox
              flexDirection="row"
              gap="s3"
              alignItems="center"
              justifyContent="center"
            >
              <LBox height={45} width={45}>
                <LBox
                  borderRadius="m-l"
                  width={45}
                  height={45}
                  borderColor="whiteTranslucent10"
                  borderWidth={1}
                  overflow="hidden"
                >
                  <BlurView
                    intensity={40}
                    tint="systemMaterialDark"
                    style={[
                      StyleSheet.absoluteFill,
                      { borderRadius: theme.borderRadii["m-m"] },
                    ]}
                  />
                  <LBox
                    justifyContent="center"
                    alignItems="center"
                    flex={1}
                    overflow="hidden"
                  >
                    {data.type && data.type !== AccessType.EVENT ? (
                      <LIcon
                        colors={
                          badgeIcons[data.type] === "flame"
                            ? { color: "white" }
                            : badgeIcons[data.type] === "chat" ||
                          badgeIcons[data.type] === "merch" ||
                          badgeIcons[data.type] === "star" ||
                          badgeIcons[data.type] === "text_box" ||
                          badgeIcons[data.type] === "video"
                            ? { color: "#0B0C0D" }
                            : undefined
                        }
                        name={badgeIcons[data.type]}
                        size={24}
                      />
                    ) : (
                      <LBox gap="zero" alignItems="center">
                        <LText
                          fontSize={10}
                          lineHeight={14}
                          variant="bodyMBold"
                          color="lightGrey2"
                          textTransform="uppercase"
                          fontWeight="500"
                        >
                          {data.startDateMonth ?? ""}
                        </LText>
                        <LText
                          fontSize={15}
                          lineHeight={16}
                          variant="bodyLBold"
                          color="white"
                          fontWeight="700"
                        >
                          {data.startDateDay ?? ""}
                        </LText>
                      </LBox>
                    )}
                  </LBox>
                </LBox>
              </LBox>
              <LBox flex={1} gap="s0_5">
                <LText
                  fontSize={14}
                  lineHeight={16}
                  color="white"
                  fontFamily="Inter-SemiBold"
                  style={
                    Platform.OS === "web"
                      ? ({ overflowX: "clip", overflowY: "visible" } as any)
                      : undefined
                  }
                  mt={Platform.OS === "web" ? "zero" : "s1_5"}
                  numberOfLines={1}
                >
                  {data.title}
                </LText>
                {data.subtitle && (
                  <LText
                    fontSize={10}
                    lineHeight={14}
                    color="whiteTranslucent80"
                    fontWeight="500"
                    style={
                      Platform.OS === "web"
                        ? ({ overflowX: "clip", overflowY: "visible" } as any)
                        : undefined
                    }
                    numberOfLines={1}
                  >
                    {data.subtitle}
                  </LText>
                )}
              </LBox>
              <LBox
                backgroundColor="whiteTranslucent20"
                py="s1"
                px="s2"
                borderRadius="max"
                alignItems="center"
              >
                <LText
                  fontSize={12}
                  lineHeight={16}
                  color="white"
                  fontWeight="500"
                >
                  {data.ctaText ?? "Open"}
                </LText>
              </LBox>
            </LBox>
          </LBox>
        );
    }
  };

  const isStoreFrontTile = data.id.startsWith("top-");
  const artistId = isStoreFrontTile ? data.id.replace("top-", "") : undefined;

  const card = (
    <Animated.View style={animatedStyle}>
      <Slide index={index} photo={data.image} scrollX={scrollX}>
        <LBox
          justifyContent="center"
          flex={1}
          borderColor="whiteTranslucent20"
          borderWidth={1}
          borderRadius="m-l"
        >
          {renderContent()}
        </LBox>
      </Slide>
    </Animated.View>
  );

  if (data.type === AccessType.DJ_SET) {
    return (
      <LPressable
        sentryEventName="AccessCarousel.DjSetCard"
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onPress={onPress}
      >
        {card}
      </LPressable>
    );
  }

  const pathname = isStoreFrontTile
    ? `/home/artist/${artistId}`
    : `/(app)/access/${data.id}`;

  return (
    <LPressable
      sentryEventName="AccessCarousel.AccessCardContent"
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onPress={() => {
        if (!isStoreFrontTile && data.type === AccessType.MUSIC) {
          prefetchAccessDetailHeroImage(data.songImage);
        }
        onPress?.();
        router.push(pathname);
      }}
    >
      {card}
    </LPressable>
  );
};

export default AccessCardContent;
